import {Controller} from '@hotwired/stimulus';
import Axios from '../axios';
import {blurryTransformedTextFix, toggleCards} from "../animations";

export default class extends Controller {

    static targets() {
        return ['list', 'filters', 'active-filters'];
    }

    connect() {
        this.isScrollRunning = false;
        this.boundingClientRect = this.element.getBoundingClientRect();
        this.currentLanguageCode = document.documentElement.lang.split('-')[0];
        this.filters = {};
        this.detailedFilters = {};

        this.type = this.element.dataset.type;
        this.from = this.element.dataset.from ? this.element.dataset.from : 0;
        this.limit = this.element.dataset.limit ? this.element.dataset.limit : 6;
        this.total = this.element.dataset.total ? this.element.dataset.total : -1;

        this.anchor = window.location.hash;
        this.isAnchorActivated = false;
        this.scrollToAnchor = !!this.element.dataset.scrollanchor;

        this.handleFilters();

        if (this.element.dataset.filter) {
            this.filters = JSON.parse(this.element.dataset.filter);
        }

        if (this.scrollToAnchor && !this.isAnchorActivated) {
            document.addEventListener('click', (e) => {
                const hasHref = e.target.matches('a[href]');
                if (hasHref) {
                    this.anchor = new URL(e.target.href).hash;
                    if (this.anchor) {
                        this.isAnchorActivated = false;
                        this.scrollToElement();
                        document.querySelectorAll('a[href]').forEach((link) => {
                            if (new URL(link.href).hash) {
                                link.classList.remove('anchor-active');
                            }
                        });
                        e.target.classList.add('anchor-active');
                    }
                }
            });

            document.querySelectorAll('a[href]').forEach((link) => {
                const anchor = new URL(link.href).hash;
                if (this.anchor === anchor) {
                    link.classList.add('anchor-active');
                } else if (this.anchor) {
                    link.classList.remove('anchor-active');
                }
            });

            if (this.anchor) {
                this.scrollToElement();
            }
        }
    }

    disconnect() {
        this.boundingClientRect = '';
    }

    handleFilters() {
        const postFilters = this.targets.find('filters');
        if (postFilters) {
            let timeout = 0;
            postFilters.querySelectorAll('select').forEach((select) => {
                select.addEventListener('change', () => {
                    const name = select.getAttribute('data-taxonomy');
                    this.filters[name] = Array.from(select.querySelectorAll('option:checked')).map(elm => elm.value);
                    this.detailedFilters[name] = Array.from(select.querySelectorAll('option:checked')).map(elm => [elm.innerText, elm.value])
                    this.setFilterChips();
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        this.from = 0;
                        this.fetchPosts().then(response => {
                            if (response && response.data) {
                                this.emptyContent();
                                this.insertContent(response.data);
                                this.from = response.from;
                                this.isScrollRunning = false;
                                if (this.scrollToAnchor && !this.isAnchorActivated && this.anchor) {
                                    this.scrollToElement();
                                }
                            }
                        });
                    }, 800);
                });
            });
        }
    }

    setFilterChips() {
        const postFilters = this.targets.find('filters');
        const activeFilters = this.targets.find('active-filters');
        if (!activeFilters || !postFilters) {
            return;
        }

        let hasChips = false;
        activeFilters.innerHTML = '';

        if (this.detailedFilters) {
            for (const [key, values] of Object.entries(this.detailedFilters)) {
                if (values) {
                    for (const value of values) {
                        hasChips = true;
                        activeFilters.insertAdjacentHTML('beforeend', `<span class="chip" data-filter="${key}" data-name="${value[1]}">${value[0]}</span>`);
                        activeFilters.querySelector(`[data-filter='${key}'][data-name='${value[1]}']`).addEventListener('click', () => {
                            const select = postFilters.querySelector(`select[data-taxonomy='${key}']`);
                            if (select.selectedOptions) {
                                for (const option of select.selectedOptions) {
                                    if (option.value === value[1]) {
                                        option.selected = false;
                                    }
                                }
                            }
                            select.dispatchEvent(new Event('change'));
                        });
                    }
                }
            }
        }

        if (hasChips) {
            activeFilters.insertAdjacentHTML('beforeend', `<span class="clear-filters">Išvalyti visus</span>`);
            activeFilters.querySelector('.clear-filters').addEventListener('click', () => {
                postFilters.querySelectorAll('select').forEach((select) => {
                    select.value = '';
                    select.dispatchEvent(new Event('change'));
                });
            });
        }
    }

    onScroll() {
        this.boundingClientRect = this.element.getBoundingClientRect();

        if (!this.isScrollRunning && window.scrollY >= this.boundingClientRect.top) {
            this.isScrollRunning = true;

            this.fetchPosts().then(response => {
                if (response && response.data && response.total) {
                    this.insertContent(response.data);
                    this.from = response.from;
                    this.isScrollRunning = false;
                    if (this.scrollToAnchor && !this.isAnchorActivated && this.anchor) {
                        this.scrollToElement();
                    }
                }
            });
        }
    }

    scrollToElement() {
        if (this.anchor) {
            const anchorElement = this.element.querySelector(this.anchor);
            if (anchorElement) {
                this.isAnchorActivated = true;
                anchorElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
            } else {
                this.element.lastElementChild.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
            }
        } else {
            this.element.lastElementChild.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        }
    }

    async fetchPosts() {
        if (parseInt(this.from) >= parseInt(this.total) && parseInt(this.total) > 0) {
            return '';
        }

        this.element.classList.add('loading');

        const data = {
            rest_language: this.currentLanguageCode,
            from: this.from,
            limit: this.limit,
            filters: JSON.stringify(this.filters),
        };

        const params = Object.keys(data).map(function (k) {
            return `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`;
        }).join('&');

        let response = null;
        try {
            response = await Axios.get(`/bsw/api/${this.type}/?${params}`);
            response = response.data.data;
        } catch (error) {
            response = error.response.data.data;
        }

        this.element.classList.remove('loading');
        return response;
    }

    emptyContent() {
        const list = this.targets.find('list');
        if (list) {
            list.innerHTML = '';
        } else {
            this.element.innerHTML = '';
        }
    }

    insertContent(html) {
        const list = this.targets.find('list');
        if (list) {
            list.insertAdjacentHTML('beforeend', html);
        } else {
            this.element.insertAdjacentHTML('beforeend', html);
        }

        toggleCards();
        blurryTransformedTextFix();
    }
}
